import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  GripVertical, 
  Plus, 
  X, 
  Save, 
  RotateCcw, 
  Tag as TagIcon,
  Shield,
  Users
} from 'lucide-react';
import { CATEGORY_MAP as INITIAL_MAP } from '../../data/groceryCategories';

interface CategoryManagerProps {
  // onSave now receives the target partition scope
  onSave: (newMap: Record<string, string[]>, scope: 'personal' | 'household') => void;
  onClose: () => void;
  currentHouseholdId: string | null;
  isAdmin: boolean;
}

/**
 * 🛠️ CategoryManager Component
 * Refactored for Hybrid Scoping: Manages grocery logic for both Personal and Shared partitions.
 */
export const CategoryManager: React.FC<CategoryManagerProps> = ({ 
  onSave, 
  onClose, 
  currentHouseholdId,
  isAdmin 
}) => {
  const { t } = useTranslation();
  
  // 🛡️ Scope Selection State (Default to Personal if no household is present)
  const [activeScope, setActiveScope] = useState<'personal' | 'household'>(
    currentHouseholdId ? 'household' : 'personal'
  );

  const [localMap, setLocalMap] = useState<Record<string, string[]>>(INITIAL_MAP);
  const [newKeyword, setNewKeyword] = useState<Record<string, string>>({});

  const handleAddKeyword = (category: string) => {
    const word = (newKeyword[category] || '').trim().toLowerCase();
    if (!word || (localMap[category] && localMap[category].includes(word))) return;

    setLocalMap(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), word]
    }));
    setNewKeyword(prev => ({ ...prev, [category]: '' }));
  };

  const handleRemoveKeyword = (category: string, word: string) => {
    setLocalMap(prev => ({
      ...prev,
      [category]: (prev[category] || []).filter((w: string) => w !== word)
    }));
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950 shadow-2xl border-l border-slate-200 dark:border-slate-800 w-full max-w-md animate-in slide-in-from-right duration-300">
      
      {/* --- SYSTEM HEADER --- */}
      <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
        <div>
          <h2 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tighter italic">
            {t('admin.aisle_manager', 'Logic Registry')}
          </h2>
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mt-1">
            {t('admin.config_engine', 'Categorization Parameters')}
          </p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-all">
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* --- HYBRID SCOPE TOGGLE (Privacy Control) --- */}
      {currentHouseholdId && (
        <div className="px-6 py-3 bg-slate-100 dark:bg-slate-900 flex gap-2 border-b border-slate-100 dark:border-slate-800">
          <button
            onClick={() => setActiveScope('personal')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              activeScope === 'personal' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400'
            }`}
          >
            <Shield className="w-3 h-3" /> {t('common.personal', 'Personal')}
          </button>
          <button
            onClick={() => setActiveScope('household')}
            disabled={!isAdmin}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              activeScope === 'household' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 opacity-50'
            }`}
          >
            <Users className="w-3 h-3" /> {t('common.household', 'Household')}
          </button>
        </div>
      )}

      {/* --- CATEGORY LIST --- */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 no-scrollbar">
        {Object.entries(localMap).map(([category, keywords]: [string, string[]]) => (
          <div key={category} className={`bg-slate-50 dark:bg-slate-900/40 rounded-3xl border border-slate-100 dark:border-slate-800 p-5 group transition-all hover:border-indigo-500/30`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-slate-300 cursor-grab" />
                <h3 className={`font-black text-xs uppercase tracking-widest ${activeScope === 'personal' ? 'text-emerald-600' : 'text-indigo-600'}`}>
                  {category}
                </h3>
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                {keywords.length} {t('admin.triggers', 'Triggers')}
              </span>
            </div>

            {/* Keyword Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {keywords.map((word: string) => (
                <span key={word} className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black uppercase tracking-tight text-slate-600 dark:text-slate-300 shadow-sm">
                  {word}
                  <button onClick={() => handleRemoveKeyword(category, word)} className="text-slate-300 hover:text-rose-500 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            {/* Add Keyword Input Group */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input 
                  type="text"
                  placeholder={t('admin.add_trigger', 'New keyword...')}
                  value={newKeyword[category] || ''}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, [category]: e.target.value }))}
                  className="w-full pl-9 pr-4 py-3 bg-white dark:bg-slate-800 border-2 border-transparent focus:border-indigo-500/50 rounded-2xl text-[11px] font-bold text-slate-900 dark:text-white outline-none transition-all"
                />
              </div>
              <button 
                onClick={() => handleAddKeyword(category)}
                className={`p-3 text-white rounded-2xl shadow-lg transition-all active:scale-95 ${activeScope === 'personal' ? 'bg-emerald-600 shadow-emerald-500/20' : 'bg-indigo-600 shadow-indigo-500/20'}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* --- FOOTER ACTIONS --- */}
      <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="flex gap-3">
          <button 
            onClick={() => setLocalMap(INITIAL_MAP)}
            className="flex-1 flex items-center justify-center gap-2 py-4 border-2 border-slate-100 dark:border-slate-800 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all"
          >
            <RotateCcw className="w-4 h-4" /> {t('common.reset', 'Default')}
          </button>
          <button 
            onClick={() => onSave(localMap, activeScope)}
            className={`flex-1 flex items-center justify-center gap-2 py-4 text-white rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest shadow-xl transition-all ${
              activeScope === 'personal' ? 'bg-emerald-600 shadow-emerald-500/30' : 'bg-indigo-600 shadow-indigo-500/30'
            }`}
          >
            <Save className="w-4 h-4" /> {t('common.save_changes', 'Sync')}
          </button>
        </div>
      </div>
    </div>
  );
};